import { createTheme, alpha } from '@mui/material/styles';

// Brand colors matching mobile app (DeliveritColors)
export const brandColors = {
    primary: '#005d6d',          // deliveritGreen
    primaryRgb: 'rgb(0, 93, 109)',
    secondary: '#0db2ad',        // deliveritLightGreen
    secondaryRgb: 'rgb(13, 178, 173)',
    lightGreen: 'rgba(110, 191, 183, 0.2)', // deliveritLighterGreen
    grey: 'rgb(142, 142, 142)',  // deliveritGrey
    blue: 'rgb(109, 128, 214)',  // deliveritBlue
    lightBlue: 'rgb(115, 149, 215)',
    third: '#ed325a',            // deliveritThird
    red: 'rgb(231, 37, 53)',     // deliveritRed
    warning: 'rgb(255, 204, 0)', // deliveritWarning
    success: '#0db2ad',          // successGreen
    pending: '#f57c00',          // pendingOrange
    destructive: '#f44336',
    destructiveBackground: '#a8233c',
    whatsapp: '#25D366',
    gmail: 'rgb(211, 47, 53)',
    appStore: '#2072F3',
    lightBackground: '#F7F7FA',
    white: '#ffffff',
    navBarDark: '#1A1A1A',
};

// Light theme
export const lightTheme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: brandColors.primary,
            light: brandColors.secondary,
            dark: '#004a58',
            contrastText: '#ffffff',
        },
        secondary: {
            main: brandColors.secondary,
            light: brandColors.lightGreen,
            dark: '#099d99',
            contrastText: '#ffffff',
        },
        error: {
            main: brandColors.red,
            light: '#ff6b7a',
            dark: brandColors.destructiveBackground,
        },
        warning: {
            main: brandColors.warning,
            light: '#ffe066',
            dark: '#c79d00',
        },
        success: {
            main: brandColors.success,
            light: '#3ec2bd',
            dark: '#099d99',
        },
        background: {
            default: brandColors.lightBackground,
            paper: brandColors.white,
        },
        text: {
            primary: '#1a1a1a',
            secondary: '#666666',
        },
        divider: 'rgba(0, 0, 0, 0.08)',
    },
    typography: {
        fontFamily: [
            'system-ui',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
        ].join(','),
        h1: {
            fontWeight: 700,
        },
        h2: {
            fontWeight: 700,
        },
        h3: {
            fontWeight: 600,
        },
        h4: {
            fontWeight: 600,
        },
        h5: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 600,
        },
        button: {
            textTransform: 'none',
            fontWeight: 600,
        },
    },
    shape: {
        borderRadius: 10,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    padding: '10px 20px',
                    fontSize: '0.95rem',
                },
                contained: {
                    backgroundColor: brandColors.secondary,
                    color: '#ffffff',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: brandColors.primary,
                        boxShadow: '0 2px 8px rgba(0, 93, 109, 0.3)',
                    },
                },
                outlined: {
                    borderColor: brandColors.secondary,
                    color: brandColors.secondary,
                    '&:hover': {
                        borderColor: brandColors.primary,
                        backgroundColor: alpha(brandColors.secondary, 0.04),
                    },
                },
                text: {
                    color: brandColors.secondary,
                    '&:hover': {
                        backgroundColor: alpha(brandColors.secondary, 0.04),
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 10,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: brandColors.secondary,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: brandColors.secondary,
                        },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: brandColors.secondary,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: brandColors.secondary,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: brandColors.secondary,
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
                colorPrimary: {
                    backgroundColor: brandColors.secondary,
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: brandColors.grey,
                    '&.Mui-checked': {
                        color: brandColors.secondary,
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    color: brandColors.grey,
                    '&.Mui-checked': {
                        color: brandColors.secondary,
                    },
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                switchBase: {
                    '&.Mui-checked': {
                        color: brandColors.secondary,
                        '& + .MuiSwitch-track': {
                            backgroundColor: brandColors.secondary,
                        },
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: brandColors.secondary,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': {
                        color: brandColors.secondary,
                    },
                },
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    '&.Mui-completed': {
                        color: brandColors.primary,
                    },
                    '&.Mui-active': {
                        color: brandColors.secondary,
                    },
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    color: brandColors.primary,
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    backgroundColor: brandColors.red,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                standardSuccess: {
                    backgroundColor: alpha(brandColors.success, 0.1),
                    color: brandColors.primary,
                },
                standardError: {
                    backgroundColor: alpha(brandColors.red, 0.1),
                },
                standardWarning: {
                    backgroundColor: alpha(brandColors.warning, 0.1),
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: brandColors.white,
                    color: brandColors.primary,
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRadius: 0,
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    '&:before': {
                        display: 'none',
                    },
                    '&.Mui-expanded': {
                        margin: '8px 0',
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                },
            },
        },
    },
});

// Dark theme
export const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: brandColors.secondary,
            light: '#3ec2bd',
            dark: brandColors.primary,
            contrastText: '#ffffff',
        },
        secondary: {
            main: brandColors.secondary,
            light: brandColors.lightGreen,
            dark: '#099d99',
            contrastText: '#ffffff',
        },
        error: {
            main: '#ff6b7a',
            light: '#ff9da6',
            dark: brandColors.red,
        },
        warning: {
            main: brandColors.warning,
            light: '#ffe066',
            dark: '#c79d00',
        },
        success: {
            main: '#3ec2bd',
            light: '#6ed4d0',
            dark: brandColors.success,
        },
        background: {
            default: '#121212',
            paper: '#1e1e1e',
        },
        text: {
            primary: '#ffffff',
            secondary: '#b0b0b0',
        },
        divider: 'rgba(255, 255, 255, 0.12)',
    },
    typography: lightTheme.typography,
    shape: lightTheme.shape,
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: '#121212',
                    color: '#ffffff',
                },
                '*': {
                    scrollbarColor: '#3f3f3f #1e1e1e',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    padding: '10px 20px',
                    fontSize: '0.95rem',
                },
                contained: {
                    backgroundColor: brandColors.secondary,
                    color: '#ffffff',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#3ec2bd',
                        boxShadow: '0 2px 8px rgba(13, 178, 173, 0.3)',
                    },
                },
                outlined: {
                    borderColor: brandColors.secondary,
                    color: brandColors.secondary,
                    '&:hover': {
                        borderColor: '#3ec2bd',
                        backgroundColor: alpha(brandColors.secondary, 0.08),
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    backgroundColor: '#1e1e1e',
                    color: '#ffffff',
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: '#1e1e1e',
                    color: '#ffffff',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#1a1a1a',
                    color: '#ffffff',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#1e1e1e',
                    color: '#ffffff',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        color: '#ffffff',
                        '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.23)',
                        },
                        '&:hover fieldset': {
                            borderColor: brandColors.secondary,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: brandColors.secondary,
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: '#b0b0b0',
                        '&.Mui-focused': {
                            color: brandColors.secondary,
                        },
                    },
                    '& .MuiInputBase-input': {
                        color: '#ffffff',
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                    '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.23)',
                    },
                    '&:hover fieldset': {
                        borderColor: brandColors.secondary,
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: brandColors.secondary,
                    },
                },
                input: {
                    color: '#ffffff',
                    '&::placeholder': {
                        color: '#888888',
                        opacity: 1,
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                icon: {
                    color: '#ffffff',
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#2d2d2d',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                    '&:hover': {
                        backgroundColor: 'rgba(13, 178, 173, 0.08)',
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(13, 178, 173, 0.16)',
                        '&:hover': {
                            backgroundColor: 'rgba(13, 178, 173, 0.24)',
                        },
                    },
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    color: '#ffffff',
                },
                secondary: {
                    color: '#b0b0b0',
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: 'rgba(255, 255, 255, 0.12)',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    color: '#ffffff',
                },
                outlined: {
                    borderColor: 'rgba(255, 255, 255, 0.23)',
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    backgroundColor: '#1e1e1e',
                    color: '#ffffff',
                    borderRadius: 10,
                    '&:before': {
                        display: 'none',
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                },
                expandIconWrapper: {
                    color: '#ffffff',
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    color: '#b0b0b0',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    color: '#b0b0b0',
                    '&.Mui-selected': {
                        color: brandColors.secondary,
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: brandColors.secondary,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                },
                standardSuccess: {
                    backgroundColor: alpha(brandColors.success, 0.15),
                    color: '#ffffff',
                },
                standardError: {
                    backgroundColor: alpha('#ff6b7a', 0.15),
                    color: '#ffffff',
                },
                standardWarning: {
                    backgroundColor: alpha(brandColors.warning, 0.15),
                    color: '#ffffff',
                },
                standardInfo: {
                    backgroundColor: alpha('#2196f3', 0.15),
                    color: '#ffffff',
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#1e1e1e',
                    color: '#ffffff',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                },
            },
        },
        MuiDialogContentText: {
            styleOverrides: {
                root: {
                    color: '#b0b0b0',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: brandColors.secondary,
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: '#b0b0b0',
                    '&.Mui-focused': {
                        color: brandColors.secondary,
                    },
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    color: '#b0b0b0',
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    color: brandColors.secondary,
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: '#b0b0b0',
                    '&.Mui-checked': {
                        color: brandColors.secondary,
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    color: '#b0b0b0',
                    '&.Mui-checked': {
                        color: brandColors.secondary,
                    },
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    '& .MuiSwitch-switchBase.Mui-checked': {
                        color: brandColors.secondary,
                        '& + .MuiSwitch-track': {
                            backgroundColor: brandColors.secondary,
                        },
                    },
                },
                track: {
                    backgroundColor: '#666666',
                },
            },
        },
        MuiSkeleton: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(255, 255, 255, 0.11)',
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#2d2d2d',
                    color: '#ffffff',
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    backgroundColor: brandColors.red,
                },
            },
        },
    },
});

export default lightTheme;
